import { FC } from 'react';
import { toast } from 'react-toastify';

import { ActionButton } from 'app/components/Button/ActionButton';
import CheckboxDefault from 'app/components/Checkbox/CheckboxDefault';
import ModalDefault from 'app/components/Modal/ModalDefault';
import { useListView } from 'app/components/Table/core/ListViewProvider';
import { useQueryResponse } from 'app/components/Table/core/QueryResponseProvider';
import { emptyListViewProvider } from 'app/components/Table/core/constants';
import { ID } from 'app/components/Table/core/types';
import Typography from 'app/components/Typography';

import { Bin } from 'app/modules/inventory/bins/core/type';

import { getResponseError } from 'app/helpers/errors';
import { useBoolean } from 'app/hooks';
import { isUndefined } from 'lodash';

import { deleteBin } from '../core/requests';

export const RemoveSensorModal: FC<{
  onCloseModal?: (updateBin?: boolean) => void;
  onUpdateRack?: () => void;
  showUnableToRemoveModal?: () => void;
  bin: Bin | null;
  sensorId: ID;
}> = ({ bin, sensorId, onCloseModal, onUpdateRack, showUnableToRemoveModal }) => {
  const { refetch } = useQueryResponse();
  const { setItemForUpdate } = useListView();
  const loading = useBoolean(false);
  const isConfirmed = useBoolean(false);

  const binId = bin?.id;

  const onSubmitDelete = async () => {
    loading.setValue(true);
    if (!binId) {
      return;
    }

    if (bin?.item?.qty && bin?.item?.qty > 0) {
      cancel();
      showUnableToRemoveModal?.();
      return;
    }

    try {
      await deleteBin(
        binId,
        sensorId,
        () => {
          toast.success('Remove sensor successfully!');
          if (!isUndefined(onCloseModal)) {
            onCloseModal(true);
            return;
          }
          if (onUpdateRack) {
            onUpdateRack();
            return;
          }
          cancel(true);
        },
        () => showUnableToRemoveModal?.(),
      );
    } catch (error: any) {
      getResponseError(error);
    } finally {
      loading.setValue(false);
      cancel();
    }
  };
  const cancel = (withRefresh?: boolean) => {
    if (!isUndefined(onCloseModal)) {
      onCloseModal();
      return;
    }
    if (withRefresh) {
      refetch();
    }
    setItemForUpdate(emptyListViewProvider);
  };

  const handleCheckboxChange = () => isConfirmed.setValue(!isConfirmed.value);

  return (
    <ModalDefault
      title="Remove sensor"
      onClickCloseModal={() => cancel()}
      isConfirmationModal={true}
    >
      <div className="mb-6">
        <Typography variant="body-3" className="mb-4">
          Stock run(s) of this item will be permanently removed along with all associated data.
        </Typography>

        <Typography variant="body-3" className="mb-4">
          This action cannot be undone.
        </Typography>

        <div className="d-flex align-items-start gap-2 mt-4">
          <CheckboxDefault checked={isConfirmed.value} onChange={handleCheckboxChange} />
          <Typography variant="body-3">
            I understand the consequences and want to remove stock run(s)
          </Typography>
        </div>
      </div>
      <ActionButton
        onCancel={() => cancel()}
        labelButtonSubmit="Remove"
        themeButton="btn-danger"
        onSubmit={onSubmitDelete}
        isSubmitting={loading.value}
        disableButton={!isConfirmed.value}
      />
    </ModalDefault>
  );
};
